<template>
  <v-content
    class="pt-0 mb-3"
    v-bind="{ 'data-required-question-unanswered-with-error': hasError }"
    :class="hasError ? 'error--text outlined-red my-2 pa-2' : ''"
  >
    <v-dialog
      max-width="1000px"
      :fullscreen="$vuetify.breakpoint.smAndDown"
      v-model="previewDialogOpened"
      @keydown.esc="
        previewDialogOpened = false;
        documentPreviewData = '';
        previewRotation = 0;
      "
    >
      <v-card v-if="selectedDocumentLink && previewType">
        <v-card-title class="title">
          <span class="text-truncate" style="max-width: 600px">{{
            getDocumentName(selectedDocumentLink)
          }}</span>

          <v-spacer />
          <v-tooltip bottom class="mr-1">
            <v-btn
              fab
              small
              flat
              class="white primary--text text-capitalize ma-0"
              @click="previewRotation += 90"
              slot="activator"
            >
              <v-icon size="25"> rotate_right </v-icon>
            </v-btn>
            <span>Rotate</span>
          </v-tooltip>
          <v-btn
            fab
            small
            flat
            class="white error--text text-capitalize ma-0 ml-1"
            @click="
              previewDialogOpened = false;
              documentPreviewData = '';
              previewRotation = 0;
            "
          >
            <v-icon size="25"> clear </v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text v-if="documentPreviewData && previewLoaded">
          <preview-document
            :document-preview-data="documentPreviewData"
            :preview-type="previewType"
            :preview-rotation="previewRotation"
          />
        </v-card-text>
        <v-card-text v-else>
          <v-progress-circular
            indeterminate
            size="50"
            class="primary--text ma-5"
          />
        </v-card-text>
      </v-card>
    </v-dialog>

    <v-layout column style="white-space: brake-spaces">
      <v-layout align-start>
        <v-flex
          style="min-width: 32px; max-width: 32px"
          class="font-weight-bold title grey--text text--darken-3 text-xs-left"
        >
          <v-flex>
            {{ questionNumber ? questionNumber : '' }}
          </v-flex>
        </v-flex>

        <v-layout column>
          <v-flex>
            <div
              class="subheading grey--text text--darken-3 text-xs-left pr-2 mb-2"
            >
              <sx-flex :gap="6">
                <!-- Text -->
                <GlossaryTextHighlight
                  :text="question.text"
                  :firm-glossary="firmGlossary"
                />

                <!-- Markings -->
                <div>
                  <v-tooltip bottom v-if="question.submitted">
                    <v-icon slot="activator" class="darkgrey--text" size="18"
                      >lock</v-icon
                    >
                    <span v-if="currentUser.is_advisor"
                      >Client cannot change the answer since it was submitted.
                      To unlock, please unsubmit the questionnaire</span
                    >
                    <span v-else>{{
                      $t('questionnaireQuestion.cannotChangeAnswer')
                    }}</span>
                  </v-tooltip>
                  <v-chip small v-if="question.optional">{{
                    $t('questionnaireQuestion.optional')
                  }}</v-chip>
                  <span
                    v-else-if="!question.submitted"
                    class="error--text ml-1"
                    data-id="questionnaire_question__required-mark"
                    >*</span
                  >
                </div>
              </sx-flex>

              <!-- New line -->
              <v-tooltip
                bottom
                class="text-xs-right ml-2"
                v-if="
                  isUploadQuestion &&
                    question.text
                      .toLowerCase()
                      .includes('any other tax-related documents')
                "
              >
                <v-icon size="20" slot="activator">help_outline</v-icon>
                <span
                  >{{ $t('questionnaireQuestion.examples') }}: <br />{{
                    $t('questionnaireQuestion.taxForm')
                  }}
                  W-2's, 1099-DIV, 1099-INT <br />{{
                    $t('questionnaireQuestion.selfEmployed')
                  }}
                  <br />{{ $t('questionnaireQuestion.closing') }} <br />{{
                    $t('questionnaireQuestion.anyDoc')
                  }}
                </span>
              </v-tooltip>
            </div>
            <!-- Question: Not completed -->
            <v-layout column v-if="showAnswerOptions">
              <!-- Question: Upload type with Document Link -->
              <v-layout
                column
                align-start
                v-if="isUploadQuestion && question.document_link"
              >
                <div class="mt-2">
                  <v-tooltip bottom>
                    <v-layout
                      slot="activator"
                      align-center
                      style="cursor: pointer"
                      @click="downloadAttachedDocument(question)"
                    >
                      <v-icon color="primary"> insert_drive_file </v-icon>
                      <span class="ml-1 my-1 primary--text">{{
                        question.document_link.split('/')[2]
                      }}</span>
                    </v-layout>
                    <span>{{
                      $t('questionnaireQuestion.clickToDownload')
                    }}</span>
                  </v-tooltip>
                </div>
              </v-layout>

              <!-- Question: Upload typebut not Uploading Later -->
              <v-layout v-if="isUploadQuestion" column>
                <!-- List of files -->
                <div
                  v-for="(document, i) in listDocuments"
                  :key="`question_${i}`"
                  class="questionnaire-question__uploaded-file"
                >
                  <!-- File name -->
                  <div
                    @click="previewDocument(document.source)"
                    style="cursor: pointer"
                  >
                    <sx-flex :gap="6" :is-y-centered="true">
                      <div class="questionnaire-question__file_icon">
                        <sx-icon name="file" :size="20" color="currentcolor" />
                      </div>
                      <span
                        class="questionnaire-question__uploaded-file_text"
                        >{{ document.name }}</span
                      >
                      <sc-label
                        type="info"
                        :is-small="true"
                        v-if="document.detailed_name"
                        >{{ document.detailed_name }}</sc-label
                      >
                    </sx-flex>
                  </div>

                  <!-- Delete file -->
                  <div
                    v-if="!disableAnswerChange"
                    @click="deleteDocument(question, document.source)"
                  >
                    <sx-button-group>
                      <sx-button type="error" size="micro">
                        <sx-icon name="close" :size="14" />
                      </sx-button>
                    </sx-button-group>
                  </div>
                </div>

                <!-- File upload button -->
                <v-layout v-if="!markedAnswerLater && !markedNotApplicable">
                  <div
                    class="questionnaire-question__upload mt-2 mb-4"
                    @click="$emit('open-upload-dialog', question)"
                  >
                    <sx-button
                      type="primary"
                      size="small"
                      :isDisabled="
                        documentsLoading || disableAnswerChange || isAnswering
                      "
                    >
                      <v-layout
                        v-if="
                          !documentsLoading ||
                            selectedQuestion.id !== question.id
                        "
                      >
                        <sx-icon name="file-upload" :size="20" />
                      </v-layout>
                      <v-layout v-else align-center>
                        <v-progress-circular
                          indeterminate
                          size="20"
                          color="white"
                        ></v-progress-circular>
                      </v-layout>
                      {{ $t('questionnaire.upload') }}
                    </sx-button>
                  </div>
                </v-layout>
              </v-layout>

              <!-- Question: Unanswered non-upload -->
              <v-radio-group
                v-if="question.question_type === 'boolean'"
                :disabled="disableAnswerChange || isAnswering"
                v-model="question.answer"
                @change="answerQuestion(question)"
                class="mx-0 my-0 py-0 px-0"
                :class="$vuetify.breakpoint.xsOnly ? '' : ''"
              >
                <v-radio :label="$t('questionnaireQuestion.yes')" value="Yes" />
                <v-radio :label="$t('questionnaireQuestion.no')" value="No" />
              </v-radio-group>
              <v-text-field
                v-else-if="question.question_type === 'text'"
                :disabled="disableAnswerChange || isAnswering"
                :value.lazy="question.answer"
                :placeholder="$t('questionnaireQuestion.answer')"
                class="default-input mx-0 my-0 py-0 px-0"
                solo
                :rules="
                  question.number_of_characters
                    ? [charRules.chars(question.number_of_characters)]
                    : []
                "
                :counter="!!question.number_of_characters"
                :maxlength="question.number_of_characters"
                @change="answerQuestion(question)"
                @input="updateValue"
              />
              <sensitive-type-input
                v-else-if="question.question_type === 'password'"
                :disabled="disableAnswerChange || isAnswering"
                :value.lazy="question.answer"
                :question="question"
                class="mx-0 my-0 py-0 px-0"
                @change="answerQuestion(question)"
                @input="updateValue"
              />
              <v-textarea
                v-else-if="question.question_type === 'textarea'"
                :disabled="disableAnswerChange || isAnswering"
                :value.lazy="question.answer"
                :placeholder="$t('questionnaireQuestion.answer')"
                class="default-input my-0 mx-0 py-0 px-0"
                solo
                @change="answerQuestion(question)"
                @input="updateValue"
              />
              <v-text-field
                v-else-if="question.question_type === 'date'"
                :disabled="disableAnswerChange || isAnswering"
                type="date"
                v-model.lazy="question.answer"
                class="default-input mx-0 my-0 py-0 px-0"
                solo
                @change="answerQuestion(question)"
              />

              <v-radio-group
                v-else-if="question.question_type === 'select'"
                :disabled="disableAnswerChange || isAnswering"
                v-model="question.answer"
                class="mx-0 my-0 py-0 px-0"
                @change="answerQuestion(question)"
              >
                <v-radio
                  v-for="option in question.options"
                  :key="option"
                  :label="option"
                  :value="option"
                >
                  <template v-slot:label>
                    <GlossaryTextHighlight
                      :text="option"
                      :firm-glossary="firmGlossary"
                      :prior-year-tag="hasPriorYearFollowUps(option)"
                      :questionnaire-year="questionnaire.tax_year"
                    />
                  </template>
                </v-radio>
              </v-radio-group>

              <v-radio-group v-else-if="question.question_type === 'multiple'">
                <v-checkbox
                  v-for="(option, index) in question.options"
                  :disabled="disableAnswerChange || isAnswering"
                  :hide-details="
                    index !==
                      (question.options ? question.options.length : 0) - 1
                  "
                  v-model="multipleSelectValues"
                  @change="saveMultipleSelect(question)"
                  class="mx-0 my-0 py-0 px-0"
                  multiple
                  :key="option"
                  :value="option"
                  :label="option"
                >
                  <template v-slot:label>
                    <GlossaryTextHighlight
                      :text="option"
                      :firm-glossary="firmGlossary"
                      :prior-year-tag="hasPriorYearFollowUps(option)"
                      :questionnaire-year="questionnaire.tax_year"
                    />
                  </template>
                </v-checkbox>
              </v-radio-group>
            </v-layout>
          </v-flex>
        </v-layout>
      </v-layout>
    </v-layout>

    <v-layout column :style="'margin-left: 32px'">
      <v-layout
        v-if="
          question.question_type === 'entries' &&
            !markedNotApplicable &&
            !markedCompleted
        "
        class="mb-2"
      >
        <v-btn
          :loading="entryLoading"
          :disabled="disableAnswerChange || isAnswering"
          :block="$vuetify.breakpoint.xsOnly"
          :small="$vuetify.breakpoint.smAndUp"
          class="default-input text-capitalize primary mx-0"
          @click="$emit('add-new-entry', question)"
        >
          {{
            isSpecialSection(currentSection)
              ? `Add ${findSpecialSection(currentSection).addName}`
              : $t('questionnaireQuestion.addNewEntry')
          }}
        </v-btn>
      </v-layout>

      <slot name="entries" />

      <sc-flex
        v-if="markedNotApplicable"
        class="mt-2 mb-3"
        :is-column="true"
        :gap="4"
      >
        <span
          v-if="!isDocumentsFromLastYearQuestion"
          class="darkgrey--text text-xs-left subheading"
          >{{ $t('questionnaireQuestion.youMarkedNotApplicable') }}</span
        >
        <span v-else class="darkgrey--text text-xs-left subheading">{{
          $t('questionnaireQuestion.youMarkedNoLongerApplicable')
        }}</span>
        <sc-button-group>
          <sc-button
            @click="$emit('toggle-not-applicable', question)"
            size="small"
            :is-disabled="disableAnswerChange || isAnswering"
            >{{ $t('questionnaireQuestion.markAsApplicable') }}</sc-button
          >
        </sc-button-group>
      </sc-flex>

      <v-layout v-if="markedCompleted" align-center class="mt-2 mb-3">
        <span class="darkgrey--text text-xs-left subheading">{{
          $t('questionnaireQuestion.markedCompletedMessage', {
            firmName: currentFirm.name,
          })
        }}</span>
      </v-layout>

      <sc-flex v-if="markedAnswerLater" :is-column="true" :gap="8" class="mb-3">
        <sc-alert type="info">{{
          isUploadQuestion
            ? $t(
                questionnaire.batch_reminder
                  ? 'questionnaireQuestion.weWillAskYouToUploadAroundDate'
                  : 'questionnaireQuestion.weWillAskYouToUploadAfterDate',
                {
                  date: moment(question.answer_by).format('ll'),
                },
              )
            : $t(
                questionnaire.batch_reminder
                  ? 'questionnaireQuestion.weWillRemindYouAroundDate'
                  : 'questionnaireQuestion.weWillRemindYouAfterDate',
                {
                  date: moment(question.answer_by).format('ll'),
                },
              )
        }}</sc-alert>
        <sc-button-group>
          <sc-button
            size="small"
            type="primary"
            @click="removeAnswerLaterBy()"
            :is-disabled="disableAnswerChange || isAnswering"
            >{{
              isUploadQuestion
                ? $t('questionnaireQuestion.readyToUpload')
                : $t('questionnaireQuestion.readyToAnswer')
            }}</sc-button
          >
        </sc-button-group>
      </sc-flex>

      <v-layout
        v-if="isUploadQuestion && markedProvidedElsewhere"
        align-center
        class="mt-2 mb-3"
      >
        <span class="darkgrey--text text-xs-left subheading">{{
          $t('questionnaireQuestion.youHaveMarkedProvidedElsewhere')
        }}</span>
        <v-btn
          @click="toggleProvidedElsewhere()"
          :disabled="disableAnswerChange || isAnswering"
          outline
          small
          color="primary"
          class="ml-3 font-weight-bold text-capitalize"
        >
          {{ $t('questionnaireQuestion.undo') }}
        </v-btn>
      </v-layout>

      <v-layout :class="$vuetify.breakpoint.mdAndUp ? 'mb-4' : ''">
        <v-tooltip
          bottom
          class="mr-2 hidden-sm-and-down"
          style="flex-shrink: 0"
          v-if="
            $vuetify.breakpoint.mdAndUp &&
              !question.disable_not_applicable &&
              !markedNotApplicable &&
              !markedCompleted &&
              !markedAnswerLater &&
              (!isUploadQuestion || !markedProvidedElsewhere)
          "
        >
          <span
            v-if="!isDocumentsFromLastYearQuestion"
            class="na-btn font-weight-bold"
            slot="activator"
            @click="$emit('toggle-not-applicable', question)"
            >{{ $t('questionnaireQuestion.notApplicable') }}</span
          >
          <span
            v-else
            class="na-btn font-weight-bold"
            slot="activator"
            @click="$emit('toggle-not-applicable', question)"
            >{{ $t('questionnaireQuestion.noLongerApplicable') }}</span
          >

          <span v-if="!isDocumentsFromLastYearQuestion">{{
            $t('questionnaireQuestion.markNotApplicable')
          }}</span>
          <span v-else>{{
            $t('questionnaireQuestion.markNoLongerApplicable')
          }}</span>
        </v-tooltip>

        <v-tooltip
          bottom
          class="mr-2 hidden-sm-and-down"
          style="flex-shrink: 0"
          v-if="
            allowAnswerLater &&
              !isUploadQuestion &&
              !markedAnswerLater &&
              !markedNotApplicable &&
              !markedCompleted
          "
        >
          <span
            class="na-btn font-weight-bold"
            slot="activator"
            @click="$emit('open-answer-later-dialog', question, true)"
            >{{ $t('questionnaireQuestion.answerLater') }}</span
          >
          <span>{{
            $t('questionnaireQuestion.weWillRemindYouAboutThisLater')
          }}</span>
        </v-tooltip>

        <v-tooltip
          bottom
          class="mr-2 hidden-sm-and-down"
          style="flex-shrink: 0"
          v-if="
            allowAnswerLater &&
              isUploadQuestion &&
              !markedAnswerLater &&
              !markedCompleted &&
              !markedProvidedElsewhere &&
              !markedNotApplicable
          "
        >
          <span
            class="na-btn font-weight-bold"
            slot="activator"
            @click="$emit('open-answer-later-dialog', question, true)"
            >{{ $t('questionnaireQuestion.uploadLater') }}</span
          >
          <span>{{
            $t('questionnaireQuestion.weWillAskYouToUploadLater')
          }}</span>
        </v-tooltip>

        <v-tooltip
          bottom
          class="mr-2 hidden-sm-and-down"
          style="flex-shrink: 0"
          v-if="
            isUploadQuestion &&
              !markedAnswerLater &&
              !markedProvidedElsewhere &&
              !markedNotApplicable &&
              !disableProvidedElsewhere &&
              !markedCompleted
          "
        >
          <span
            class="na-btn font-weight-bold"
            slot="activator"
            @click="toggleProvidedElsewhere()"
            >{{ $t('questionnaireQuestion.providedElsewhere') }}</span
          >
          <span>{{
            $t('questionnaireQuestion.markThisIfProvidedElsewhere')
          }}</span>
        </v-tooltip>

        <v-tooltip
          bottom
          class="mr-2 hidden-sm-and-down"
          style="flex-shrink: 0"
          v-if="$vuetify.breakpoint.mdAndUp"
        >
          <span
            slot="activator"
            class="comments-btn font-weight-bold"
            @click="$emit('open-comment-dialog', question)"
          >
            <span v-if="isEmpty(filteredComments(question))">
              {{ $t('questionnaireQuestion.addNote') }}
            </span>
            <span v-else>
              {{ $t('questionnaireQuestion.viewNotes') }} (
              {{ filteredComments(question).length }} )
            </span>
          </span>
          <span>{{ $t('questionnaireQuestion.viewAddNotes') }}</span>
        </v-tooltip>
      </v-layout>

      <v-layout class="mb-2 hidden-md-and-up buttons-container">
        <span
          v-if="
            !question.disable_not_applicable &&
              !markedNotApplicable &&
              !markedCompleted &&
              (!isUploadQuestion || !markedProvidedElsewhere)
          "
          @click="$emit('toggle-not-applicable', question)"
          class="na-btn font-weight-bold mr-3"
        >
          <template v-if="!isDocumentsFromLastYearQuestion">
            {{ $t('questionnaireQuestion.notApplicable') }}
          </template>
          <template v-else>
            {{ $t('questionnaireQuestion.noLongerApplicable') }}
          </template>
        </span>

        <span
          v-if="
            !markedAnswerLater &&
              !markedNotApplicable &&
              !markedCompleted &&
              allowAnswerLater
          "
          @click="$emit('open-answer-later-dialog', question, true)"
          class="na-btn font-weight-bold mr-3"
        >
          <template v-if="question.question_type !== 'upload'">
            {{ $t('questionnaireQuestion.answerLater') }}
          </template>
          <template v-else>
            {{ $t('questionnaireQuestion.uploadLater') }}
          </template>
        </span>

        <span
          v-if="
            isUploadQuestion &&
              !markedProvidedElsewhere &&
              !markedNotApplicable &&
              !markedCompleted &&
              !disableProvidedElsewhere
          "
          @click="toggleProvidedElsewhere()"
          class="na-btn font-weight-bold mr-3"
        >
          {{ $t('questionnaireQuestion.providedElsewhere') }}
        </span>

        <span
          @click="$emit('open-comment-dialog', question)"
          :class="
            isEmpty(filteredComments(question))
              ? 'darkgrey--text'
              : 'primary--text'
          "
          class="na-btn font-weight-bold mr-3"
        >
          {{ $t('questionnaireQuestion.viewNotes') }}
          <span v-if="!isEmpty(filteredComments(question))" class="ml-1"
            >({{ filteredComments(question).length }})</span
          >
        </span>
      </v-layout>
    </v-layout>
  </v-content>
</template>
<script>
import API from '@/api';
import isEmpty from 'lodash/isEmpty';
import moment from 'moment';
import SensitiveTypeInput from './SensitiveTypeInput';
import PreviewDocument from '@/components/PreviewDocument';
import {
  supportedExtensionsForPreview,
  customSections,
  rules,
} from '@/constants/variables';
import { JOIN_STRING } from '@/constants/questionnaires';
import GlossaryTextHighlight from './GlossaryTextHighlight';
import { mapGetters } from 'vuex';
import debounce from 'lodash/debounce';
import FirmPreferenceMixins from '../mixins/FirmPreferenceMixins';
import SxButton from '@/components/sx/SxButton';
import SxButtonGroup from '@/components/sx/SxButtonGroup';
import SxFlex from '@/components/sx/SxFlex';
import SxIcon from '@/components/sx/SxIcon';
import QuestionnaireMixin from '@/mixins/QuestionnaireMixin';
import UtilsMixins from '@/mixins/UtilsMixins';

export default {
  name: 'QuestionnaireQuestion',
  mixins: [FirmPreferenceMixins, QuestionnaireMixin, UtilsMixins],
  components: {
    GlossaryTextHighlight,
    SensitiveTypeInput,
    PreviewDocument,
    SxButton,
    SxButtonGroup,
    SxFlex,
    SxIcon,
  },
  props: {
    question: {
      required: true,
      type: Object,
    },
    questionnaire: {
      type: Object,
    },
    questionNumber: {
      type: Number,
    },
    documentsLoading: {
      required: true,
      type: Boolean,
    },
    entryLoading: {
      type: Boolean,
    },
    showUnanswered: {
      type: Boolean,
    },
    selectedQuestion: {
      type: Object,
    },
    isDocumentsFromLastYearQuestion: {
      type: Boolean,
    },
    allowAnswerLater: {
      type: Boolean,
    },
    currentCompany: {
      type: Object,
    },
    firmGlossary: {
      type: [Object, Array],
      default: null,
    },
    currentSection: {
      type: Object,
    },
    extractedDocuments: {
      type: Array,
    },
  },
  data() {
    return {
      rules,
      customSections,
      supportedExtensionsForPreview,
      active: 0,
      documentsDeleting: false,
      multipleSelectValues: [],
      charRules: {
        chars(maxNum) {
          const maxIntNum = parseInt(maxNum);
          return v =>
            (v || '').length === maxIntNum ||
            `Must contain ${maxNum} characters`;
        },
      },
      attachedDocumentLoading: false,
      previewDialogOpened: false,
      documentPreviewData: null,
      previewRotation: 0,
      selectedDocumentLink: null,
      previewType: null,
      previewLoaded: false,
      debouncedUpdate: null,
      isAnswering: false,
    };
  },
  computed: {
    ...mapGetters({
      currentFirm: 'currentFirm',
      currentUser: 'user',
    }),
    markedAnswerLater() {
      return this.question.answer_by;
    },
    isUploadQuestion() {
      return this.question.question_type === 'upload';
    },
    markedNotApplicable() {
      return this.question.answer === 'Not Applicable';
    },
    markedProvidedElsewhere() {
      return this.question.answer === 'Provided Elsewhere';
    },
    markedCompleted() {
      return this.question.answer === 'Marked Completed';
    },
    hasError() {
      const isQuestionEmpty =
        isEmpty(this.question.answer) ||
        (this.question.answer === '0' &&
          this.question.question_type === 'entries') ||
        (!!this.question.answer_by &&
          this.question.question_type === 'entries');

      return isQuestionEmpty && this.showUnanswered && !this.question.optional;
    },
    showAnswerOptions() {
      return (
        !this.markedNotApplicable &&
        !this.markedCompleted &&
        (!this.isUploadQuestion || !this.markedProvidedElsewhere)
      );
    },
    disableAnswerChange() {
      return !this.currentUser.is_advisor && this.question.submitted;
    },
    listDocuments() {
      if (!isEmpty(this.question.answer)) {
        return this.question.answer
          .split(JOIN_STRING)
          .filter(d => d != '')
          .map(doc => {
            const extracted_document = this.extractedDocuments.find(
              d => this.getShortDocumentPath(d) === doc,
            );
            return {
              name: this.getDocumentName(doc),
              source: doc,
              detailed_name: this.getDetailedName(doc, extracted_document),
            };
          });
      } else {
        return [];
      }
    },
  },
  methods: {
    isEmpty,
    moment,
    getShortDocumentPath(doc) {
      return doc.source
        .split('/')
        .slice(3)
        .join('/');
    },
    getDetailedName(doc, matched_doc) {
      if (!matched_doc) return null;
      if (matched_doc.detailed_name) return matched_doc.detailed_name;
      if (this.getDocumentName(doc) === matched_doc.name) return null;
      if (matched_doc.name) return matched_doc.name;
    },
    removeAnswerLaterBy() {
      this.question.answer_by = null;
      this.answerQuestion(this.question);
    },
    updateValue(value) {
      this.debouncedUpdate(value);
    },
    hasPriorYearFollowUps(option) {
      return this.questionnaire.questionnaire_questions.some(
        q =>
          q.follow_up_question_id === this.question.id &&
          q.uploaded_last_year &&
          q.trigger_for_follow_up_question === option,
      );
    },
    colorOfComments(question) {
      if (isEmpty(filteredComments(question))) {
        return 'grey--text text--darken-1 material-icons-outlined';
      } else {
        return 'grey--text text--darken-3 material-icons-outlined';
      }
    },
    toggleProvidedElsewhere() {
      if (this.question.answer === 'Provided Elsewhere') {
        this.question.answer = '';
      } else {
        this.$emit(
          'open-comment-dialog',
          this.question,
          'Please explain where/how you provided this document (e.g. Email, Mail, In-person).',
          'Provided Elsewhere',
        );
      }
      this.answerQuestion(this.question);
    },
    changeSelected(question) {
      this.selected = question;
    },
    answerQuestion(question) {
      const thisComponent = this;
      thisComponent.isAnswering = true;

      question.questionSaveCallback = function() {
        thisComponent.isAnswering = false;
        question.questionSaveCallback = null;
      };

      this.$emit('save-question', question);
    },
    saveMultipleSelect(question) {
      question.answer = this.multipleSelectValues.join(JOIN_STRING);
      this.answerQuestion(question);
    },
    answerToList(question) {
      if (question.answer) {
        return question.answer.replaceAll('"', '').split(JOIN_STRING);
      } else {
        return [];
      }
    },
    getDocumentName(document = '') {
      const splitDocument = document.split('/');

      return splitDocument[splitDocument.length - 1];
    },
    async downloadDocumentByLink(url, name) {
      let response = await this.$http.get(url, { responseType: 'arraybuffer' });
      let blob = new Blob([response.data], {
        type: this.get(response.headers, 'content-type'),
      });
      let link = document.createElement('a');
      link.href = window.URL.createObjectURL(blob);
      link.download = name;
      link.click();
    },
    async previewDocument(documentLink) {
      this.previewType = '';
      this.previewLoaded = false;
      this.documentPreviewData = '';
      this.selectedDocumentLink = documentLink;
      this.previewRotation = 0;
      this.previewType = this.endsWithAny(
        this.supportedExtensionsForPreview,
        documentLink,
      );
      if (this.previewType) {
        if (this.previewType === 'pdf') {
          this.currentPage = 1;
          this.pdfLoaded = false;
        }
        this.previewDialogOpened = true;
        try {
          const response = await API.integration.downloadDocument({
            companyId: this.currentCompany.id,
            link: this.currentCompany.upload_link.concat('/', documentLink),
          });

          if (response.data.document) {
            this.documentPreviewData = response.data.document.data.link;
          } else {
            console.log(response.data.error);
            this.$store.dispatch('application/setErrorNotification', {
              message: 'There was a problem downloading the document.',
              errorToSend: response.data.error,
            });
          }
        } catch (e) {
          console.log(e);
          this.$store.dispatch('application/setErrorNotification', {
            message: 'There was a problem downloading the document.',
            errorToSend: e,
          });
        } finally {
          this.previewLoaded = true;
        }
      } else {
        const response = await API.integration.downloadDocument({
          companyId: this.currentCompany.id,
          link: this.currentCompany.upload_link.concat('/', documentLink),
        });

        if (response.data.document) {
          this.documentPreviewData = response.data.document.data.link;
          this.downloadDocumentByLink(
            this.documentPreviewData,
            this.getDocumentName(documentLink),
          );
        } else {
          console.log(response.data.error);
          this.$store.dispatch('application/setErrorNotification', {
            message: 'There was a problem downloading the document.',
            errorToSend: response.data.error,
          });
        }
        // this.$store.dispatch('application/setErrorNotification', {
        //   message: 'Preview is not available.'
        // });
      }
    },
    async downloadAttachedDocument(question) {
      if (!this.attachedDocumentLoading) {
        try {
          this.attachedDocumentLoading = true;
          this.$store.dispatch('application/setSuccessNotification', {
            message: 'Downloading. Please wait a moment.',
          });
          const response = await API.questionnaire.downloadAttachedDocument(
            question.id,
          );
          if (response.data.error) {
            this.$store.dispatch('application/setErrorNotification', {
              message: response.data.error,
            });
          } else {
            window.open(response.data.document.data.link, '_blank');
          }
        } finally {
          this.attachedDocumentLoading = false;
        }
      }
    },
    findSpecialSection(section) {
      let specialSection = this.customSections.find(
        s => s.name === section.name,
      );
      if (!specialSection) {
        specialSection = this.customSections.find(s =>
          s.otherNames.includes(section.name),
        );
      }
      return specialSection;
    },
    isSpecialSection(section) {
      return this.customSections
        .map(s => s.name)
        .concat(this.customSections.map(s => s.otherNames).flat())
        .includes(section.name);
    },
    async deleteDocument(question, document) {
      if (!this.documentsDeleting) {
        this.documentsDeleting = true;
        try {
          const response = await API.integration.deletePath({
            path: '/'.concat(document),
            companyId: this.currentCompany.id,
          });
          if (response.data.error) {
            console.log(response.data.error);
          } else {
            if (question.answer.split(JOIN_STRING).length === 1) {
              question.answer = '';
            } else {
              question.answer = question.answer
                .split(JOIN_STRING)
                .filter(d => d !== document)
                .join(JOIN_STRING);
            }
            await API.questionnaire.answerQuestion({
              id: question.id,
              answer: question.answer,
              companyId: this.currentCompany.id,
            });
          }
        } catch (e) {
          console.log(e);
        } finally {
          this.documentsDeleting = false;
        }
      }
    },
  },
  async mounted() {
    if (this.question.question_type === 'multiple' && this.question.answer) {
      this.multipleSelectValues = this.question.answer.split(JOIN_STRING);
    }
  },
  created() {
    this.debouncedUpdate = debounce(value => {
      this.question.answer = value;
    }, 300);
  },
};
</script>
<style lang="postcss" scoped>
.signature {
  font-family: 'Cedarville Cursive', cursive;
  font-size: 1.8em;
}
.outlined-red {
  border: 1px solid #cb4039;
}
.onboarding-steps {
  &__container {
    max-width: 1000px;
    margin: 0 auto;
  }
}
.frame {
  overflow: auto;
  border: 1px solid black;
}

.frame::-webkit-scrollbar {
  -webkit-appearance: none;
}

.frame::-webkit-scrollbar:vertical {
  width: 11px;
}

.frame::-webkit-scrollbar:horizontal {
  height: 11px;
}

.frame::-webkit-scrollbar-thumb {
  border-radius: 8px;
  border: 2px solid white; /* should match background, can't be transparent */
  background-color: rgba(0, 0, 0, 0.5);
}
.outlined {
  border: solid 0.2px rgba(9, 30, 66, 0.06) !important;
}

.na-btn {
  color: #545454;
  cursor: pointer;
  font-size: 13px;
  border-radius: 4px;
  border: 1px solid #888;
  padding: 4px 10px;
  background: #fff;
  display: inline-block;
  height: 28px;
  flex-shrink: 0;

  &:hover {
    background: #eee;
  }
}

.comments-btn {
  color: #545454;
  cursor: pointer;
  font-size: 13px;
  border-radius: 4px;
  border: 1px solid #888;
  padding: 4px 10px;
  background: #fff;
  display: inline-block;
  height: 28px;
  flex-shrink: 0;

  &:hover {
    background: #eee;
  }
}

.comments-icon {
  font-size: 16px;
}

.action-button {
  height: 46px;

  & >>> .v-btn__content {
    white-space: normal;
  }
}

.buttons-container {
  flex-wrap: wrap;
}

.buttons-container > span {
  margin-bottom: 10px;
}

.questionnaire-question__uploaded-file {
  display: flex;
  justify-content: space-between;
  gap: 4px;
  padding: 6px;
  max-width: 100%;
  margin-bottom: 1px;

  background-color: var(--gray-0);
  border-radius: 4px;
}

.questionnaire-question__uploaded-file_text {
  color: var(--blue-500);

  font-size: 12px;
  font-weight: 500;
  line-height: 18px;
  text-align: left !important;
  word-break: break-all;
  text-decoration: underline;
}

.questionnaire-question__file_icon {
  align-items: center;
  display: flex;
  height: 32px;
  justify-content: center;
  min-width: 32px;
  padding-left: 4px;
  width: 32px;

  border-radius: 4px;
  background-color: var(--gray-100);
  color: var(--blue-500);
}

.questionnaire-question__file_icon div {
  margin: 0 !important;
}

.questionnaire-question__upload {
  display: flex;
}
</style>
